import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { UpdateGroupRole } from '../../redux/Actions/userStatusService';
import { useDispatch } from "react-redux";

function EditRoleModal({ open, handleClose, handleUpdateRole, selectedRole, permissions = [], loading = false }) {
  const dispatch = useDispatch();
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  // Update state when selectedRole changes
  useEffect(() => {
    if (selectedRole) {
      setRoleName(selectedRole.name || "");
      setRoleDescription(selectedRole.description || "");
    }
  }, [selectedRole]);

  // Update permissions when API permissions are loaded
  useEffect(() => {
    if (permissions.length > 0 && selectedRole && selectedRole.permissions) {
      const updatedPermissions = {};
      
      // Initialize all permissions to false
      permissions.forEach(perm => {
        // Check if this permission is in the selectedRole.permissions array
        updatedPermissions[perm.apiName] = selectedRole.permissions.includes(perm.apiName);
      });
      
      setSelectedPermissions(updatedPermissions);
    }
  }, [permissions, selectedRole]);

  const handlePermissionChange = (apiName) => {
    setSelectedPermissions(prev => {
      const newState = {
        ...prev,
        [apiName]: !prev[apiName]
      };
      return newState;
    });
  };

  const handleSelectAll = (checked) => {
    const newPermissions = {};
    permissions.forEach(perm => {
      newPermissions[perm.apiName] = checked;
    });
    setSelectedPermissions(newPermissions);
  };

  const isAllSelected = permissions.length > 0 && 
    permissions.every(perm => selectedPermissions[perm.apiName] === true);

  const isIndeterminate = permissions.length > 0 && 
    permissions.some(perm => selectedPermissions[perm.apiName] === true) && 
    !permissions.every(perm => selectedPermissions[perm.apiName] === true);

  const handleSnackbarClose = () => {
    setSnackbar({...snackbar, open: false});
  };

  const handleSubmit = async () => {
    if (!roleName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a role name",
        severity: "error"
      });
      return;
    }

    if (!roleDescription.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter a role description",
        severity: "error"
      });
      return;
    }

    // Get the list of selected API names
    const apiNames = [];
    Object.keys(selectedPermissions).forEach(key => {
      if (selectedPermissions[key]) {
        apiNames.push(key);
      }
    });

    // Create the payload for the API
    const Id=selectedRole.id;
    const body = {
      groupRole: selectedRole.originalData?.groupRole,
      groupName: roleName.trim(),
      description: roleDescription.trim(),
      apiNames: apiNames
    };

    setSubmitting(true);
    try {
      const response = await dispatch(UpdateGroupRole({Id, body})).unwrap();
      
      if (response && response.statusCode === 200) {
        setSnackbar({
          open: true,
          message: "Role updated successfully",
          severity: "success"
        });
        
        // Notify parent component to refresh roles list
        handleUpdateRole({
          ...selectedRole,
          name: roleName,
          permissions: apiNames
        });
        handleClose();
      } else {
        throw new Error(response?.message || "Failed to update role");
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error updating role",
        severity: "error"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        sx={{ml:30}}
      >
        <DialogTitle sx={{ pb: 1, pt: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Edit Role</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2, maxHeight: '600px', overflowY: 'auto' }}>
          <TextField
            fullWidth
            placeholder="Enter Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            variant="outlined"
            sx={{ mb: 3 }}
          />
          
          <TextField
            fullWidth
            placeholder="Enter Role Description"
            value={roleDescription}
            onChange={(e) => setRoleDescription(e.target.value)}
            variant="outlined"
            multiline
            rows={3}
            inputProps={{
              maxLength: 150
            }}
            helperText={`${roleDescription.length}/150 characters`}
            sx={{ 
              mb: 3,
              '& .MuiInputBase-root': {
                wordBreak: 'break-word'
              }
            }}
          />
          
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Select Permissions for the role
          </Typography>
          
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Select All Checkbox */}
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={isAllSelected}
                      indeterminate={isIndeterminate}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        Select All Permissions
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {Object.values(selectedPermissions).filter(Boolean).length} of {Object.keys(selectedPermissions).length} permissions selected
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              
              <Grid container spacing={2}>
                {permissions.map((permission) => (
                  <Grid item xs={12} md={6} key={permission.apiName}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={selectedPermissions[permission.apiName] || false}
                          onChange={() => handlePermissionChange(permission.apiName)}
                        />
                      }
                      label={permission.description}
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose} 
            variant="outlined"
            sx={{ 
              minWidth: '100px',
              mr: 1
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!roleName.trim() || !roleDescription.trim() || loading || submitting}
            sx={{ 
              minWidth: '100px',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              }
            }}
          >
            {submitting ? <CircularProgress size={24} /> : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default EditRoleModal; 